import { useEffect, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAdminDevices, setDeviceRemark, removeDevice, createProductionDevice, getDeviceLifecycleHistory, getNextSerial } from '../api/admin.js';
import { PRODUCTION_SERIAL_RE } from '@m8-app/contracts/rest';
import type { AdminDeviceSummary, AdminProduct } from '@m8-app/contracts/rest/admin.js';
import type { DeviceLifecycleState } from '@m8-app/contracts/rest/lifecycle.js';

const LIFECYCLE_COLORS: Record<DeviceLifecycleState, string> = {
  manufactured: 'bg-slate-100 text-slate-700',
  tested:       'bg-blue-50  text-blue-700',
  sold:         'bg-violet-50 text-violet-700',
  registered:   'bg-amber-50 text-amber-700',
  active:       'bg-green-50 text-green-700',
  deactivated:  'bg-red-50   text-red-700',
};

const PRODUCT_LABELS: Record<string, string> = {
  m8: 'M8',
  'cop-lop': 'COP-LOP',
  avira: 'Avira',
};

export function Devices() {
  const [searchParams] = useSearchParams();
  const product = searchParams.get('product') ?? '';

  const [cursor, setCursor] = useState<string | null>(null);
  const [state, setState] = useState('');
  const [lifecycle, setLifecycle] = useState('');
  const [presence, setPresence] = useState('');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [lifecycleModalDeviceId, setLifecycleModalDeviceId] = useState<string | null>(null);

  const params = {
    ...(cursor    ? { cursor }    : {}),
    ...(state     ? { state }     : {}),
    ...(lifecycle ? { lifecycle } : {}),
    ...(presence  ? { presence }  : {}),
    ...(search    ? { search }    : {}),
    ...(product   ? { product }   : {}),
  };
  const { data, isLoading } = useQuery({
    queryKey: ['adminDevices', params],
    queryFn: () => getAdminDevices(params),
  });

  const productLabel = product ? PRODUCT_LABELS[product] ?? product.toUpperCase() : null;

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">
            {productLabel ? `${productLabel} Devices` : 'Device Management'}
          </h2>
          <p className="text-sm text-gray-500">All devices across the system. Open a device for owner, logs, warranty, and service history.</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors whitespace-nowrap"
        >
          <span className="text-lg leading-none">+</span> Add Device
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <select value={state} onChange={(e) => { setState(e.target.value); setCursor(null); }} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All reg states</option>
          <option value="registered">Registered</option>
          <option value="pending">Registration pending</option>
          <option value="deactivated">Deactivated</option>
        </select>
        <select value={lifecycle} onChange={(e) => { setLifecycle(e.target.value); setCursor(null); }} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">All lifecycle</option>
          <option value="manufactured">Manufactured</option>
          <option value="tested">Tested</option>
          <option value="sold">Sold</option>
          <option value="registered">Registered</option>
          <option value="active">Active</option>
          <option value="deactivated">Deactivated</option>
        </select>
        <select value={presence} onChange={(e) => { setPresence(e.target.value); setCursor(null); }} className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
          <option value="">Any presence</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
        </select>
        <input
          type="text"
          placeholder="Search serial / name"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCursor(null); }}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[200px]"
        />
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">Serial</th>
              <th className="px-4 py-3 font-medium">Owner</th>
              <th className="px-4 py-3 font-medium">Claimed</th>
              <th className="px-4 py-3 font-medium">Registered</th>
              <th className="px-4 py-3 font-medium">Active</th>
              <th className="px-4 py-3 font-medium">Presence</th>
              <th className="px-4 py-3 font-medium">Firmware</th>
              <th className="px-4 py-3 font-medium">Remark</th>
              <th className="px-4 py-3 font-medium" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data?.devices.map((d) => (
              <DeviceRow key={d.id} d={d} onLifecycleClick={() => setLifecycleModalDeviceId(d.id)} />
            ))}
            {data && data.devices.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-gray-400">No devices</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.nextCursor && (
        <button
          onClick={() => setCursor(data.nextCursor)}
          className="mt-3 text-sm text-indigo-600 hover:underline"
        >
          Load more
        </button>
      )}

      {showAdd && <AddDeviceDialog initialProduct={(product || 'm8') as AdminProduct} onClose={() => setShowAdd(false)} />}
      {lifecycleModalDeviceId && (
        <LifecycleHistoryModal
          deviceId={lifecycleModalDeviceId}
          onClose={() => setLifecycleModalDeviceId(null)}
        />
      )}
    </div>
  );
}

function DeviceRow({ d, onLifecycleClick }: { d: AdminDeviceSummary; onLifecycleClick: () => void }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [remark, setRemark] = useState(d.remark ?? '');

  const { mutate: save, isPending } = useMutation({
    mutationFn: () => setDeviceRemark(d.id, remark),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['adminDevices'] });
    },
  });

  const { mutate: remove, isPending: isRemoving } = useMutation({
    mutationFn: () => removeDevice(d.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['adminDevices'] }),
  });

  const lifecycleColor = LIFECYCLE_COLORS[d.lifecycleState];

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 font-mono text-xs">{d.serialNumber}</td>
      <td className="px-4 py-3 font-mono text-xs">{d.ownerMobile ?? '—'}</td>
      <td className="px-4 py-3">
        {d.claimStatus === 'claimed'
          ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">Claimed</span>
          : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Unclaimed</span>}
      </td>
      <td className="px-4 py-3">
        {d.sellerRegistered
          ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">Registered</span>
          : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Not Registered</span>}
      </td>
      <td className="px-4 py-3">
        <button
          onClick={onLifecycleClick}
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${lifecycleColor}`}
          title="View lifecycle history"
        >
          {d.lifecycleState === 'active' ? 'Active' : d.lifecycleState === 'deactivated' ? 'Inactive' : d.lifecycleState}
        </button>
      </td>
      <td className="px-4 py-3">
        {d.presence === 'online'
          ? <span className="text-green-700">● online</span>
          : d.presence === 'offline'
            ? <span className="text-gray-400">○ offline</span>
            : '—'}
      </td>
      <td className="px-4 py-3 text-xs">{d.activeFirmwareVersion ?? '—'}</td>
      <td className="px-4 py-3 max-w-[200px]">
        {editing ? (
          <div className="flex gap-1">
            <input
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"
            />
            <button
              onClick={() => save()}
              disabled={isPending}
              className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            >
              {isPending ? '…' : 'Save'}
            </button>
            <button onClick={() => { setEditing(false); setRemark(d.remark ?? ''); }} className="text-xs px-2 py-1 text-gray-500">
              Cancel
            </button>
          </div>
        ) : (
          <button onClick={() => setEditing(true)} className="text-xs text-left text-gray-600 hover:text-indigo-600 truncate w-full">
            {d.remark || <span className="italic text-gray-400">add remark</span>}
          </button>
        )}
      </td>
      <td className="px-4 py-3 flex items-center gap-2 whitespace-nowrap">
        <Link to={`/devices/${d.id}`} className="text-xs text-indigo-600 hover:underline">Open</Link>
        <button
          onClick={() => {
            const msg = d.ownerMobile
              ? `Remove this device from ${d.ownerMobile}'s account? It clears their setup and access, but the hardware stays re-claimable.`
              : 'Remove this device? It clears any saved setup, but the hardware stays re-claimable.';
            if (confirm(msg)) remove();
          }}
          disabled={isRemoving}
          className="text-xs text-red-600 hover:underline disabled:opacity-50"
        >
          {isRemoving ? '…' : 'Remove'}
        </button>
      </td>
    </tr>
  );
}

function LifecycleHistoryModal({ deviceId, onClose }: { deviceId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['lifecycleHistory', deviceId],
    queryFn: () => getDeviceLifecycleHistory(deviceId),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Lifecycle History</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        <div className="overflow-y-auto flex-1">
          {data?.events.length === 0 && <p className="text-sm text-gray-400">No events.</p>}
          <ol className="space-y-2">
            {data?.events.map((ev) => (
              <li key={ev.id} className="text-xs border-l-2 border-indigo-200 pl-3">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${ev.fromState ? LIFECYCLE_COLORS[ev.fromState] : 'bg-gray-100 text-gray-500'}`}>
                    {ev.fromState ?? '—'}
                  </span>
                  <span className="text-gray-400">→</span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${LIFECYCLE_COLORS[ev.toState]}`}>
                    {ev.toState}
                  </span>
                  <span className="text-gray-400 ml-auto">{new Date(ev.createdAt).toLocaleString()}</span>
                </div>
                <div className="text-gray-500 mt-0.5">
                  {ev.actorType}{ev.reason ? ` — ${ev.reason}` : ''}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
}

const ADD_PRODUCTS: { value: AdminProduct; label: string }[] = [
  { value: 'm8',      label: 'M8 (Stair Light)' },
  { value: 'avira',   label: 'Avira (Ceiling Light)' },
  { value: 'cop-lop', label: 'COP-LOP (Lift)' },
];

function AddDeviceDialog({ initialProduct, onClose }: { initialProduct: AdminProduct; onClose: () => void }) {
  const qc = useQueryClient();
  const [product, setProduct] = useState<AdminProduct>(initialProduct);
  // '' = let the server pick the sticky batch for this month; otherwise a 1–99 override.
  const [batch, setBatch] = useState('');
  // Set once the admin types into the serial field; stops the suggestion from
  // overwriting their edit until they Regenerate or change product/batch.
  const [serialDirty, setSerialDirty] = useState(false);
  const [form, setForm] = useState({ serialNumber: '', secretKey: '', setupPassword: '', remark: '' });
  const [result, setResult] = useState<{ deviceIdentityId: string; serialNumber: string } | null>(null);
  const [err, setErr] = useState('');

  const batchNum = batch ? Number(batch) : undefined;
  const suggestion = useQuery({
    queryKey: ['nextSerial', product, batchNum ?? null],
    queryFn: () => getNextSerial(product, batchNum),
    staleTime: 0,
  });

  useEffect(() => {
    if (suggestion.data && !serialDirty) {
      setForm((f) => ({ ...f, serialNumber: suggestion.data.serialNumber }));
    }
  }, [suggestion.data, serialDirty]);

  const { mutate, isPending } = useMutation({
    mutationFn: () => createProductionDevice({
      serialNumber: form.serialNumber,
      secretKey: form.secretKey,
      setupPassword: form.setupPassword,
      ...(form.remark ? { remark: form.remark } : {}),
    }),
    onSuccess: (res) => {
      setResult(res);
      setErr('');
      setForm({ serialNumber: '', secretKey: '', setupPassword: '', remark: '' });
      setSerialDirty(false);
      qc.invalidateQueries({ queryKey: ['adminDevices'] });
      // The next S00n lands in the (now empty) serial field for the next unit.
      qc.invalidateQueries({ queryKey: ['nextSerial'] });
    },
    onError: (e: unknown) => {
      const resp = (e as { response?: { data?: { message?: unknown; code?: unknown } } }).response;
      const msg = resp?.data?.message;
      if (Array.isArray(msg)) {
        setErr(msg.map((m: { path?: string[]; message?: string }) =>
          `${m.path?.[0] ?? 'field'}: ${m.message ?? 'invalid'}`).join(' · '));
      } else if (resp?.data?.code === 'SERIAL_EXISTS') {
        // Someone else took the suggestion first — pull a fresh one.
        setSerialDirty(false);
        qc.invalidateQueries({ queryKey: ['nextSerial'] });
        setErr('Serial already taken — a fresh one has been suggested. Submit again.');
      } else {
        setErr((typeof msg === 'string' ? msg : null) ?? 'Create failed');
      }
    },
  });

  const minLengths: Partial<Record<keyof typeof form, number>> = {
    secretKey: 8, setupPassword: 4,
  };

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setResult(null);
    const errors: string[] = [];
    if (!PRODUCTION_SERIAL_RE.test(form.serialNumber)) {
      errors.push('serialNumber: expected PREFIX-MMYY-BB-Snnn, e.g. HM8-0926-01-S001');
    } else if (suggestion.data && !form.serialNumber.startsWith(suggestion.data.prefix)) {
      errors.push(`serialNumber: must start with ${suggestion.data.prefix} for the selected product`);
    }
    for (const [key, min] of Object.entries(minLengths) as [keyof typeof form, number][]) {
      if (form[key].length < min) {
        errors.push(`${key}: must be at least ${min} characters`);
      }
    }
    if (errors.length) { setErr(errors.join(' · ')); return; }
    setErr('');
    mutate();
  }

  function textField(key: keyof typeof form, label: string, placeholder: string) {
    const min = minLengths[key];
    const tooShort = min !== undefined && form[key].length > 0 && form[key].length < min;
    return (
      <div>
        <label htmlFor={`add-dev-${key}`} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <input
          id={`add-dev-${key}`}
          type="text"
          placeholder={placeholder}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${tooShort ? 'border-red-400' : 'border-gray-300'}`}
          required={key !== 'remark'}
          minLength={min}
        />
        {tooShort && <p className="text-xs text-red-500 mt-0.5">Minimum {min} characters</p>}
      </div>
    );
  }

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500';

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Add Device</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Serial format: <span className="font-mono">PREFIX-MMYY-BB-Snnn</span>, e.g. <span className="font-mono">HM8-0926-01-S001</span>.
          Month/year and sequence are filled in automatically; change Batch to start a new run
          (it stays until the month ends, then resets to 01).
        </p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label htmlFor="add-dev-product" className="block text-sm font-medium text-gray-700 mb-1">Product</label>
              <select
                id="add-dev-product"
                value={product}
                onChange={(e) => { setProduct(e.target.value as AdminProduct); setSerialDirty(false); }}
                className={inputCls}
              >
                {ADD_PRODUCTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="add-dev-batch" className="block text-sm font-medium text-gray-700 mb-1">Batch</label>
              <input
                id="add-dev-batch"
                type="text"
                inputMode="numeric"
                maxLength={2}
                placeholder={suggestion.data ? String(suggestion.data.batch).padStart(2, '0') : '01'}
                value={batch}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                  setBatch(v === '0' || v === '00' ? '' : v);
                  setSerialDirty(false);
                }}
                className={`${inputCls} font-mono`}
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="add-dev-serialNumber" className="block text-sm font-medium text-gray-700">Serial Number</label>
              <button
                type="button"
                onClick={() => { setSerialDirty(false); void suggestion.refetch(); }}
                disabled={suggestion.isFetching}
                className="text-xs text-indigo-600 hover:underline disabled:opacity-50"
              >
                {suggestion.isFetching ? 'Generating…' : 'Regenerate'}
              </button>
            </div>
            <input
              id="add-dev-serialNumber"
              type="text"
              placeholder={suggestion.isLoading ? 'Generating…' : 'e.g. HM8-0926-01-S001'}
              value={form.serialNumber}
              onChange={(e) => { setSerialDirty(true); setForm((f) => ({ ...f, serialNumber: e.target.value.toUpperCase() })); }}
              className={`${inputCls} font-mono`}
              required
            />
            {suggestion.isError && (
              <p className="text-xs text-amber-600 mt-0.5">Could not fetch a suggestion — enter the serial by hand.</p>
            )}
          </div>
          {textField('secretKey',     'Secret Key',      'min 8 chars (stored hashed)')}
          {textField('setupPassword', 'Setup Password',  'min 4 chars (one-time, hashed)')}
          {textField('remark',        'Remark',          'optional staff note')}
          {err && <p className="text-sm text-red-600">{err}</p>}
          {result && (
            <div className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">
              <p>Created — ID: <code className="font-mono text-xs">{result.deviceIdentityId}</code></p>
              <p>Serial: <code className="font-mono text-xs">{result.serialNumber}</code></p>
            </div>
          )}
          <p className="text-xs text-gray-400">Secret Key and Setup Password are Argon2id-hashed server-side — never stored plain.</p>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button
              type="submit"
              disabled={isPending}
              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
